import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Select, Upload, Button, Table, Tag, Space, Alert, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import { formatTS } from '../utils/time';

// Admin > File Uploads > Stars Adherence Level Upload: one level per agent per
// quarter (0 Platinum, 1 Gold, 2 Silver, 3 did not achieve), from a sheet with
// a Payroll ID column and a Level column. Checked first (every row must match
// a user and have a level of 0-3), then saved -- replacing the quarter. An
// agent with no row has no record and cannot qualify for Superior Stars.
// Admins and the 'stars-adherence-upload' page grant only.

const LEVEL_COLOR = { 0: '#6366f1', 1: '#16a34a', 2: '#6b7280', 3: '#ef4444' };
const LEVEL_NAME = { 0: 'Platinum', 1: 'Gold', 2: 'Silver', 3: 'Did not achieve' };

// This quarter and the seven before it, newest first.
const quarterOptions = () => {
  const out = [];
  let d = dayjs().startOf('month');
  for (let i = 0; i < 8; i += 1) {
    const q = `${d.year()}-Q${Math.floor(d.month() / 3) + 1}`;
    if (!out.some((o) => o.value === q)) out.push({ value: q, label: q });
    d = d.subtract(3, 'month');
  }
  return out;
};

// The Payroll ID and Level columns, by their headers; a name (one column, or
// First + Last) when there is one, for suggesting who a row is for.
const pickColumns = (headers) => ({
  id: headers.find((h) => /payroll|employee\s*id|emp\s*id/i.test(h)),
  level: headers.find((h) => /level/i.test(h)),
  first: headers.find((h) => /first/i.test(h)),
  last: headers.find((h) => /last/i.test(h)),
  name: headers.find((h) => /name/i.test(h) && !/first|last|payroll|level/i.test(h)),
});
const rowName = (r, cols) => (cols.name ? String(r[cols.name] ?? '').trim()
  : [cols.first && r[cols.first], cols.last && r[cols.last]].filter(Boolean).join(' ').trim());

export default function StarsAdherenceUploadPage() {
  const quarters = quarterOptions();
  // Default: the last finished quarter, which is what is normally uploaded.
  const [quarter, setQuarter] = useState(quarters[1]?.value || quarters[0].value);
  const [preview, setPreview] = useState(null);     // { rows, problems, fileName }
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState([]);
  const [loadedBusy, setLoadedBusy] = useState(false);
  // People a row with a missing / unknown payroll id can be pointed at.
  const [people, setPeople] = useState([]);
  useEffect(() => {
    api.get('/api/stars-adherence/people').then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  }, []);

  const loadQuarter = useCallback(() => {
    setLoadedBusy(true);
    api.get('/api/stars-adherence', { params: { quarter } })
      .then((r) => setLoaded(r.data || []))
      .catch(() => setLoaded([]))
      .finally(() => setLoadedBusy(false));
  }, [quarter]);
  useEffect(() => { loadQuarter(); setPreview(null); }, [loadQuarter]);

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
        if (!json.length) { message.error('The sheet is empty.'); return; }
        const cols = pickColumns(Object.keys(json[0]));
        if (!cols.id || !cols.level) {
          message.error('The sheet needs a Payroll ID column and a Level column.');
          return;
        }
        const rows = json
          .map((r) => ({ payroll_id: String(r[cols.id] ?? '').trim(), level: String(r[cols.level] ?? '').trim(),
            name: rowName(r, cols) }))
          .filter((r) => r.payroll_id || r.level);
        setChecking(true);
        const res = await api.post('/api/stars-adherence/upload', { quarter, rows });
        setPreview({ ...res.data, fileName: file.name });
      } catch (err) {
        message.error(err.response?.data?.error || 'Could not read the file.');
      } finally {
        setChecking(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;   // read here, never sent as a file
  };

  // A row pointed at a person (picked, or a suggestion accepted): checked again
  // with the rest, so a duplicate shows up at once.
  // payrollId null: leave the row out (someone not in Harbor, say).
  const assign = async (index, payrollId) => {
    const rows = preview.rows
      .map((r, i) => ({ payroll_id: i === index ? payrollId : r.PayrollID, level: r.Level ?? '', name: r.Name || '' }))
      .filter((r, i) => !(i === index && payrollId === null));
    try {
      const res = await api.post('/api/stars-adherence/upload', { quarter, rows });
      setPreview((p) => ({ ...res.data, fileName: p.fileName }));
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not check the row.');
    }
  };
  const personOptions = people.map((p) => ({
    value: p.PayrollID, label: `${p.DisplayName}${p.TeamName ? ` (${p.TeamName})` : ''} · ${p.PayrollID}`,
  }));

  const save = async () => {
    setSaving(true);
    try {
      const rows = preview.rows.map((r) => ({ payroll_id: r.PayrollID, level: r.Level }));
      const res = await api.post('/api/stars-adherence/upload', { quarter, rows, save: true });
      message.success(`Saved ${res.data.saved} levels for ${quarter}.`);
      setPreview(null);
      loadQuarter();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const levelTag = (v) => (v == null ? <Typography.Text type="secondary">—</Typography.Text>
    : <Tag style={{ background: LEVEL_COLOR[v], color: '#fff', border: 'none' }}>{v} · {LEVEL_NAME[v]}</Tag>);

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Stars Adherence Level Upload</Typography.Title>
      <Space wrap style={{ marginBottom: 12 }}>
        <span>Quarter:</span>
        <Select style={{ width: 140 }} value={quarter} onChange={setQuarter} options={quarters} />
        <Typography.Text type="secondary">
          Columns: Payroll ID, Level (0 Platinum, 1 Gold, 2 Silver, 3 did not achieve)
        </Typography.Text>
      </Space>

      <Upload.Dragger accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={readFile} disabled={checking}
        style={{ marginBottom: 16 }}>
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Click or drag the {quarter} file here</p>
      </Upload.Dragger>

      {preview && (
        <div style={{ marginBottom: 24 }}>
          {preview.problems > 0 ? (
            <Alert type="error" showIcon style={{ marginBottom: 8 }}
              message={`${preview.problems} of ${preview.rows.length} rows have a problem. Fix the file and upload it again; nothing is saved until every row is right.`} />
          ) : (
            <Alert type="success" showIcon style={{ marginBottom: 8 }}
              message={`${preview.rows.length} rows from ${preview.fileName} are ready. Saving replaces everything loaded for ${quarter}${loaded.length ? ` (${loaded.length} now)` : ''}.`}
              action={<Button type="primary" loading={saving} onClick={save}>Save {quarter}</Button>} />
          )}
          <Table rowKey={(r, i) => `${r.PayrollID}|${i}`} size="small" dataSource={preview.rows}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'], showTotal: (t) => `${t} ${t === 1 ? 'row' : 'rows'}` }}
            columns={[
              { title: 'Payroll ID', dataIndex: 'PayrollID', key: 'PayrollID', width: 130,
                render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Name in file', dataIndex: 'Name', key: 'Name', width: 180,
                render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
              // Missing or unknown payroll id: pick the person, or accept the
              // suggestion matched from the name in the file.
              { title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 360,
                render: (v, r, i) => (['No payroll ID', 'No user with this payroll ID'].includes(r.Problem) ? (
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Select size="small" showSearch optionFilterProp="label" placeholder="Pick who this is"
                      style={{ width: '100%' }} options={personOptions}
                      onChange={(pid) => assign(preview.rows.indexOf(r), pid)} />
                    {r.Suggested && (
                      <span style={{ fontSize: 12 }}>
                        <Typography.Text type="secondary">Suggested: </Typography.Text>{r.Suggested.DisplayName}{' '}
                        <Typography.Link onClick={() => assign(preview.rows.indexOf(r), r.Suggested.PayrollID)}>Use</Typography.Link>
                      </span>
                    )}
                  </Space>
                ) : (v || <Typography.Text type="secondary">—</Typography.Text>)) },
              { title: 'Level', dataIndex: 'Level', key: 'Level', width: 180, render: levelTag },
              { title: 'Problem', dataIndex: 'Problem', key: 'Problem', width: 280,
                filters: [{ text: 'Only problems', value: 1 }], onFilter: (_, r) => !!r.Problem,
                render: (v, r) => (v ? (
                  <Space size={6} wrap>
                    <Tag color="red">{v}</Tag>
                    <Typography.Link onClick={() => assign(preview.rows.indexOf(r), null)}>Leave out</Typography.Link>
                  </Space>
                ) : <Tag color="green">OK</Tag>) },
            ]} />
        </div>
      )}

      <Typography.Title level={5}>Loaded for {quarter} ({loaded.length})</Typography.Title>
      <Table rowKey="EmployeeID" size="small" loading={loadedBusy} dataSource={loaded}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'], showTotal: (t) => `${t} ${t === 1 ? 'person' : 'people'}` }} locale={{ emptyText: 'Nothing loaded for this quarter.' }}
        columns={[
          { title: 'Payroll ID', dataIndex: 'EmployeeID', key: 'EmployeeID', width: 130 },
          { title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName',
            sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
          { title: 'Level', dataIndex: 'Level', key: 'Level', width: 180, sorter: (a, b) => a.Level - b.Level, render: levelTag },
          { title: 'Uploaded', key: 'up', width: 260,
            render: (_, r) => `${r.UploadedByName || '—'} · ${formatTS(r.UploadedAt)}` },
        ]} />
    </div>
  );
}
